import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Avatar,
  Chip,
  Divider,
  Grid,
  Card,
  CardContent,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Close as CloseIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  School as SchoolIcon,
  Pets as PetsIcon,
  SmokingRooms as SmokingIcon,
  SmokingRoomsTwoTone as NoSmokingIcon,
  CalendarMonth as CalendarIcon,
  Euro as EuroIcon,
  Phone as PhoneIcon,
  Verified as VerifiedIcon,
  AttachMoney as AttachMoneyIcon,
  Home as HomeIcon,
  Email as EmailIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  MailOutline as MailOutlineIcon,
  CheckCircleOutline as CheckIcon
} from '@mui/icons-material';
import TextField from '@mui/material/TextField';
import ShareMenu from './ShareMenu';
import { buildTenantUrl, buildPropertyUrl } from '../hooks/useShareableUrl';
import { supabase } from '../services/supabaseService';
import { messagesService } from '../services/supabaseService';

// Formatowanie wieku — wartość z bazy to liczba (18, 20, 30...), wyświetlamy jako 'Wiek: 20+'
const formatAge = (age) => {
  const str = String(age);
  if (str.includes('+')) return `Wiek: ${str}`;
  const n = Number(age);
  if (!n || isNaN(n)) return `Wiek: ${str}`;
  return `Wiek: ${n}+`;
};

// Wykrywanie żeńskiego imienia (polskie imiona żeńskie kończą się na -a, z wyjątkami)
const isFemale = (name) => {
  if (!name) return false;
  const first = name.trim().split(/\s+/)[0].toLowerCase();
  const maleExceptions = ['kuba', 'barnaba', 'kosma', 'bonawentura', 'jarema', 'sasza'];
  if (maleExceptions.includes(first)) return false;
  return first.endsWith('a');
};

const TenantDetailsModal = ({ open, onClose, tenant, onPublish, isSubmitting, progress, publishSuccess = false, isEditMode = false, buttonText = null, viewOnly = false, isOwner = false, hideMessageForm = false, onEditProfile, onGoToMyListings, onGoToMessages, userId, userEmail, userListings = [], fetchUserListings, onFavoritesChange, isFavorite: externalIsFavorite, onLoginRequired }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [message, setMessage] = useState('');
  const [messageSent, setMessageSent] = useState(false);
  const [messageSending, setMessageSending] = useState(false);
  const [attachMenuAnchor, setAttachMenuAnchor] = useState(null);
  const [showPhone, setShowPhone] = useState(false);
  const [isFav, setIsFav] = useState(false);
  const [liveMessagesCount, setLiveMessagesCount] = useState(0);

  React.useEffect(() => {
    if (open) {
      setMessage(tenant ? `Dzień dobry, mam mieszkanie które może Cię zainteresować. Chętnie podam więcej szczegółów. Proszę o kontakt.` : '');
      setMessageSent(false);
      setMessageSending(false);
    }
  }, [open, tenant]);

  // Lazy-fetch user listings when opening modal (for attach link)
  React.useEffect(() => {
    if (open && userId && !isOwner && !hideMessageForm && fetchUserListings && userListings.length === 0) {
      fetchUserListings();
    }
  }, [open, userId, isOwner, hideMessageForm]);

  const insertPropertyLink = (property) => {
    const url = `${window.location.origin}${buildPropertyUrl(property)}`;
    const linkText = `\n\nPrzesyłam link do mojego ogłoszenia:\n👉 ${url}`;
    setMessage(prev => prev.trimEnd() + linkText);
    setAttachMenuAnchor(null);
  };

  const handleAttachClick = (e) => {
    if (userListings.length === 1) {
      insertPropertyLink(userListings[0]);
    } else {
      setAttachMenuAnchor(e.currentTarget);
    }
  };

  // Live messages count for owner
  const isOwnProfile = isOwner || hideMessageForm;
  React.useEffect(() => {
    if (open && tenant?.id && isOwnProfile) {
      supabase.from('messages')
        .select('*', { count: 'exact', head: true })
        .eq('tenant_profile_id', tenant.id)
        .then(({ count, error }) => {
          if (error) console.error('[TenantStats] messages count error:', error);
          setLiveMessagesCount(count || 0);
        });
    }
  }, [open, tenant, isOwnProfile]);

  React.useEffect(() => {
    setIsFav(!!externalIsFavorite);
  }, [externalIsFavorite, tenant]);

  const toggleFavorite = async (e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    if (!tenant) return;
    if (!userId) {
      if (onLoginRequired) onLoginRequired();
      return;
    }
    const newVal = !isFav;
    setIsFav(newVal);
    // Pobierz aktualną listę i zaktualizuj
    const { data } = await supabase.from('users').select('favorite_tenants').eq('id', userId).single();
    let favArray = data?.favorite_tenants || [];
    if (newVal) {
      if (!favArray.includes(tenant.id)) favArray = [...favArray, tenant.id];
    } else {
      favArray = favArray.filter(id => id !== tenant.id);
    }
    await supabase.from('users').update({ favorite_tenants: favArray }).eq('id', userId);
    if (onFavoritesChange) onFavoritesChange();
  };

  // Funkcja do zamiany pierwszej litery na wielką
  const capitalize = (str) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const handleClose = () => {
    onClose();
  };

  if (!tenant) return null;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      disableScrollLock={true}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          maxHeight: isMobile ? '100vh' : '90vh'
        }
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Header z przyciskiem zamknij */}
        <Box sx={{ 
          position: 'sticky',
          top: 0,
          backgroundColor: 'white',
          zIndex: 1,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
          p: 2
        }}>
          {!isOwner && !hideMessageForm && (
            <ShareMenu
              url={tenant ? `${window.location.origin}${buildTenantUrl(tenant)}` : ''}
              title={tenant?.first_name || tenant?.name || 'Profil najemcy'}
              text={`Profil najemcy: ${tenant?.first_name || tenant?.name || ''}`}
            />
          )}
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Główny kontener */}
        <Box sx={{ p: 3, pt: 0 }}>
          {/* Header z podstawowymi informacjami */}
          <Box sx={{ 
            mb: 4,
            ...(isMobile && {
              border: '1px solid #e2e8f0',
              borderRadius: 3,
              p: 2,
              backgroundColor: '#fafbfc',
            })
          }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'center', md: 'flex-start' }, gap: 3, mb: 3 }}>
              {/* Zdjęcie najemcy */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Avatar
                  src={tenant.avatar || tenant.profile_photo_url}
                  alt={tenant.name || tenant.first_name}
                  sx={{ 
                    width: { xs: 100, md: 120 }, 
                    height: { xs: 100, md: 120 },
                    border: '4px solid #F7FAFC'
                  }}
                />
              </Box>
              
              {/* Podstawowe informacje */}
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2D3748' }}>
                    {tenant.name}
                  </Typography>
                  {tenant.isVerified && (
                    <VerifiedIcon sx={{ color: '#28A745', fontSize: 28 }} />
                  )}
                  {viewOnly && !isOwner && !hideMessageForm && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', ml: 1 }} onClick={toggleFavorite}>
                      {isFav 
                        ? <FavoriteIcon sx={{ color: '#E53E3E', fontSize: 24 }} />
                        : <FavoriteBorderIcon sx={{ color: '#A0AEC0', fontSize: 24 }} />
                      }
                      <Typography variant="caption" sx={{ color: isFav ? '#E53E3E' : '#A0AEC0', fontSize: '0.7rem' }}>
                        {isFav ? 'w ulubionych' : 'dodaj do ulubionych'}
                      </Typography>
                    </Box>
                  )}
                </Box>
                
                {/* Lokalizacja */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                  <LocationIcon sx={{ color: '#EE2005', fontSize: { xs: 16, md: 18 } }} />
                  <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                    Szukam w mieście: {tenant.city}
                    {tenant.preferredDistrict && `, ${tenant.preferredDistrict}`}
                  </Typography>
                </Box>

                {/* Budżet */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                  <AttachMoneyIcon sx={{ color: '#28A745', fontSize: { xs: 16, md: 18 } }} />
                  <Typography variant="body1" sx={{ color: '#28A745', fontWeight: 'bold', fontSize: { xs: '0.875rem', md: '1rem' } }}>
                    Budżet: {tenant.priceRange}
                  </Typography>
                </Box>

                {/* Pokoje */}
                {tenant.roomsWanted != null && tenant.roomsWanted !== '' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                    <HomeIcon sx={{ color: '#4A5568', fontSize: 18 }} />
                    <Typography variant="body1" color="text.secondary">
                      {Number(tenant.roomsWanted) === 0 ? 'Pokój w mieszkaniu' : Number(tenant.roomsWanted) === 1 ? 'Kawalerka' : `${tenant.roomsWanted} ${Number(tenant.roomsWanted) >= 2 && Number(tenant.roomsWanted) <= 4 ? 'pokoje' : 'pokoi'}`}
                    </Typography>
                  </Box>
                )}

                {/* Data wprowadzenia */}
                {tenant.moveInDate && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                    <CalendarIcon sx={{ color: '#4A5568', fontSize: { xs: 16, md: 18 } }} />
                    <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                      Od: {new Date(tenant.moveInDate).toLocaleDateString('pl-PL')}
                    </Typography>
                  </Box>
                )}

                {/* Wyposażenie */}
                {tenant.equipment && Array.isArray(tenant.equipment) && tenant.equipment.length > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 2 }}>
                    <HomeIcon sx={{ color: '#4A5568', fontSize: { xs: 16, md: 18 }, mt: 0.3 }} />
                    <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                      Preferowane wyposażenie: {tenant.equipment.join(', ')}
                    </Typography>
                  </Box>
                )}

                {/* Status chips */}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
                  {tenant.petOwner && (
                    <Chip 
                      label="Posiadam psa lub kota"
                      icon={<PetsIcon fontSize="small" />}
                      size="small"
                      sx={{ backgroundColor: '#FFF3E0', color: '#E65100' }}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Content Section - Full Width */}
          <Box sx={{ mb: 3 }}>
            {/* O sobie - Full Width */}
            {tenant.description && (
              <Card sx={{ backgroundColor: '#F9FAFB', border: 'none', boxShadow: 'none', mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                    O sobie
                  </Typography>
                  
                  {/* Wiek, zawód i status palenia */}
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    {tenant.age && (
                      <Chip 
                        label={formatAge(tenant.age)}
                        size="small"
                        sx={{ backgroundColor: '#E3F2FD', color: '#1565C0' }}
                      />
                    )}
                    {(tenant.profession || tenant.occupation) && (
                      <Chip 
                        label={capitalize(tenant.profession || tenant.occupation)}
                        size="small"
                        sx={{ backgroundColor: '#FFDED5', color: '#7B1FA2' }}
                      />
                    )}
                    {(tenant.smoker !== null && tenant.smoker !== undefined) && (
                      <Chip 
                        label={tenant.smoker ? (isFemale(tenant.name) ? 'Paląca' : 'Palący') : (isFemale(tenant.name) ? 'Niepaląca' : 'Niepalący')}
                        icon={tenant.smoker ? <SmokingIcon fontSize="small" /> : <NoSmokingIcon fontSize="small" />}
                        size="small"
                        sx={{ backgroundColor: tenant.smoker ? '#FFEBEE' : '#E8F5E9', color: tenant.smoker ? '#C62828' : '#2E7D32' }}
                      />
                    )}
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                    {tenant.description}
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/* Formularz kontaktowy - widoczny tylko w trybie przeglądania (nie dla właściciela) */}
            {viewOnly && !isOwner && !hideMessageForm && (
              <Card sx={{ backgroundColor: '#F9FAFB', border: 'none', boxShadow: 'none', mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', fontSize: '1.1rem' }}>
                    Napisz do najemcy
                  </Typography>

                  {messageSent ? (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                      <CheckIcon sx={{ fontSize: 48, color: '#4CAF50', mb: 1 }} />
                      <Typography variant="body1" sx={{ fontWeight: '600', color: '#4CAF50', mb: 2 }}>
                        Wiadomość została wysłana!
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      <TextField
                        multiline
                        rows={4}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        fullWidth
                        disabled={messageSending}
                        error={!message.trim()}
                        helperText={!message.trim() ? 'Wiadomość nie może być pusta' : ''}
                        sx={{ mb: 1 }}
                      />

                      {/* Dołącz link do mieszkania */}
                      {userId && userListings.length > 0 && (
                        <>
                          <Button
                            size="small"
                            startIcon={<HomeIcon />}
                            onClick={handleAttachClick}
                            disabled={messageSending}
                            sx={{
                              textTransform: 'none',
                              color: '#D2691E',
                              fontSize: '0.85rem',
                              mb: 1.5,
                              '&:hover': { backgroundColor: '#FFF8F0' }
                            }}
                          >
                            📎 Dołącz link do mieszkania
                          </Button>
                          <Menu
                            anchorEl={attachMenuAnchor}
                            open={Boolean(attachMenuAnchor)}
                            onClose={() => setAttachMenuAnchor(null)}
                            PaperProps={{ sx: { maxHeight: 300, minWidth: 280 } }}
                          >
                            <MenuItem disabled sx={{ opacity: '1 !important' }}>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: '#666' }}>
                                Wybierz ogłoszenie do dołączenia:
                              </Typography>
                            </MenuItem>
                            {userListings.map((listing) => (
                              <MenuItem
                                key={listing.id}
                                onClick={() => insertPropertyLink(listing)}
                                sx={{ whiteSpace: 'normal' }}
                              >
                                <ListItemIcon>
                                  <HomeIcon sx={{ color: '#D2691E' }} />
                                </ListItemIcon>
                                <ListItemText
                                  primary={listing.title}
                                  secondary={listing.district ? `${listing.district}, ${listing.city || ''}` : listing.city || ''}
                                  primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
                                  secondaryTypographyProps={{ fontSize: '0.75rem' }}
                                />
                              </MenuItem>
                            ))}
                          </Menu>
                        </>
                      )}

                      <Button
                        variant="contained"
                        fullWidth
                        onClick={async () => {
                          if (!message.trim() || messageSending) return;
                          if (!userId) {
                            if (onLoginRequired) onLoginRequired();
                            return;
                          }
                          setMessageSending(true);
                          try {
                            await messagesService.send({
                              tenantProfileId: tenant.id,
                              recipientEmail: tenant.email,
                              recipientName: tenant.name || tenant.firstName,
                              senderUserId: userId,
                              senderEmail: userEmail || '',
                              messageText: message.trim(),
                              listingTitle: `Profil najemcy: ${tenant.name || tenant.firstName}`
                            });
                            setMessageSent(true);
                            setMessage('');
                            setTimeout(() => {
                              setMessageSent(false);
                              setMessage(tenant ? `Dzień dobry, mam mieszkanie które może Cię zainteresować. Chętnie podam więcej szczegółów. Proszę o kontakt.` : '');
                            }, 4000);
                          } catch (err) {
                            console.error('Błąd wysyłania wiadomości:', err);
                            alert('Nie udało się wysłać wiadomości. Spróbuj ponownie.');
                          } finally {
                            setMessageSending(false);
                          }
                        }}
                        disabled={!message.trim() || messageSending}
                        sx={{
                          backgroundColor: '#D2691E',
                          '&:hover': { backgroundColor: '#B8591A' },
                          '&.Mui-disabled': { backgroundColor: '#ccc' },
                          mb: 2,
                          textTransform: 'none'
                        }}
                      >
                        {messageSending ? 'Wysyłanie...' : 'Wyślij wiadomość'}
                      </Button>
                    </>
                  )}

                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={handleClose}
                    sx={{
                      borderColor: '#D2691E',
                      color: '#D2691E',
                      textTransform: 'none',
                      '&:hover': { borderColor: '#B8591A', color: '#B8591A' }
                    }}
                  >
                    Zamknij
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Panel właściciela profilu - statystyki i akcje */}
            {(isOwner || hideMessageForm) && (
              <Box sx={{ mb: 3 }}>
                {/* Info: To jest Twój profil */}
                <Typography variant="body1" sx={{ fontWeight: 600, color: '#D2691E', mb: 2, textAlign: 'center' }}>
                  To jest Twój profil najemcy
                </Typography>

                {/* Statystyki */}
                <Box sx={{
                  display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap'
                }}>
                  <Box sx={{
                    flex: 1, minWidth: 140, display: 'flex', alignItems: 'center', gap: 1.5,
                    p: 2, borderRadius: 2, backgroundColor: '#F0F4FF', border: '1px solid #C7D7FF'
                  }}>
                    <VisibilityIcon sx={{ color: '#3B5BDB', fontSize: 28 }} />
                    <Box>
                      <Typography variant="h5" fontWeight="bold" sx={{ color: '#3B5BDB', lineHeight: 1 }}>
                        {tenant.viewsCount || 0}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#555' }}>
                        odwiedzin profilu
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{
                    flex: 1, minWidth: 140, display: 'flex', alignItems: 'center', gap: 1.5,
                    p: 2, borderRadius: 2, backgroundColor: '#F0FFF4', border: '1px solid #9BE0B0'
                  }}>
                    <MailOutlineIcon sx={{ color: '#2E7D32', fontSize: 28 }} />
                    <Box>
                      <Typography variant="h5" fontWeight="bold" sx={{ color: '#2E7D32', lineHeight: 1 }}>
                        {liveMessagesCount}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#555' }}>
                        wiadomości otrzymanych
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Przycisk edycji */}
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<EditIcon />}
                  onClick={() => onEditProfile && onEditProfile(tenant)}
                  sx={{
                    backgroundColor: '#D2691E',
                    '&:hover': { backgroundColor: '#B8591A' },
                    mb: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '15px',
                    py: 1.5
                  }}
                >
                  Edytuj profil
                </Button>

                {onGoToMyListings && (
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => { handleClose(); onGoToMyListings(); }}
                    sx={{
                      borderColor: '#D2691E',
                      color: '#D2691E',
                      '&:hover': { borderColor: '#B8591A', color: '#B8591A' },
                      mb: 1.5,
                      textTransform: 'none',
                      fontWeight: 600
                    }}
                  >
                    Przejdź do Moich ogłoszeń
                  </Button>
                )}

                {onGoToMessages && (
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => { handleClose(); onGoToMessages(); }}
                    sx={{
                      borderColor: '#D2691E',
                      color: '#D2691E',
                      '&:hover': { borderColor: '#B8591A', color: '#B8591A' },
                      mb: 1.5,
                      textTransform: 'none',
                      fontWeight: 600
                    }}
                  >
                    Przejdź do moich wiadomości
                  </Button>
                )}

                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleClose}
                  sx={{
                    borderColor: '#ccc',
                    color: '#666',
                    textTransform: 'none',
                    '&:hover': { borderColor: '#999', color: '#444' }
                  }}
                >
                  Zamknij
                </Button>
              </Box>
            )}

            {/* Action Buttons - tylko dla właściciela profilu */}
            {!viewOnly && (
            <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Back to Edit Button */}
              <Button
                fullWidth
                variant="outlined"
                size="large"
                onClick={handleClose}
                sx={{
                  borderColor: '#FF8C00',
                  color: '#FF8C00',
                  py: 1.5,
                  fontSize: '14px',
                  fontWeight: '500',
                  borderRadius: 2,
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: '#E67E00',
                    backgroundColor: '#FFF7ED',
                    color: '#E67E00'
                  }
                }}
              >
                Powrót do edycji
              </Button>
              
              {/* Publish Button */}
              <Box sx={{ position: 'relative' }}>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={isSubmitting || publishSuccess}
                  onClick={() => onPublish && onPublish()}
                  sx={{
                    backgroundColor: publishSuccess ? '#2e7d32' : '#111',
                    color: 'white',
                    py: 2,
                    fontSize: '16px',
                    fontWeight: '600',
                    borderRadius: 2,
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: publishSuccess ? '#1b5e20' : '#333'
                    }
                  }}
                >
                  {publishSuccess
                    ? (buttonText ? `✓ ${buttonText}` : (isEditMode ? '✓ Profil zaktualizowany' : '✓ Profil opublikowany'))
                    : isSubmitting
                      ? (isEditMode ? 'Zapisywanie...' : 'Publikowanie...')
                      : (isEditMode ? 'Zapisz zmiany' : 'Opublikuj profil najemcy')}
                </Button>
                
                {isSubmitting && (
                  <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0 }}>
                    <Box sx={{ width: '100%', height: 4, backgroundColor: '#eee', borderRadius: 2 }}>
                      <Box sx={{ width: `${progress || 0}%`, height: 4, backgroundColor: '#D2691E', borderRadius: 2, transition: 'width 0.1s' }} />
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default TenantDetailsModal;